/**
 * design_handoff_domain_links — the Domain card. Band structure: header →
 * live band → option → option → option → footer, each divided by a full-
 * width rule. Card's own Header/Footer slots cover the outer bands; the
 * live band and the three options are plain bands in between (Card just
 * renders children, so mixing Card.Header/Card.Footer with raw divs is
 * fine — no positional constraint).
 */
import React from "react";
import appConfig from "../../../../../appConfig";
import Card from "../../../theme/untitled/Card/Card";
import Icon from "../../../theme/untitled/icons/Icon";
import { PLANS } from "../../../../../consts";
import OptionRow from "./OptionRow";
import DefaultDomainOption from "./DefaultDomainOption";
import SubdomainOption from "./SubdomainOption";
import CustomDomainOption from "./CustomDomainOption";
import styles from "./DomainCard.module.scss";

const HELP_ARTICLE_URL = "https://help.revisual.io/en/articles/16942278-how-domain-setup-works";

const DomainCard = ({
  liveHost,
  pendingDomain,
  currentPlan,
  selectedOption,
  onSelectOption,
  defaultOptionProps,
  subdomainOptionProps,
  customOptionProps,
}) => (
  <Card>
    <Card.Header
      title="Domain"
      subtitle="Pick one. Switching changes the address of every existing link, so QR codes and shared URLs on the old address stop working."
    />

    <div className={styles.liveBand}>
      <Icon name="GlobeSlated01" size={20} className={styles.liveIcon} />
      <span className={styles.liveText}>
        {pendingDomain ? (
          <>
            Live now on <span className={styles.liveHost}>{liveHost}</span> — links keep
            resolving here until <span className={styles.liveHost}>{pendingDomain}</span> is
            verified.
          </>
        ) : (
          <>
            Live now on <span className={styles.liveHost}>{liveHost}</span>.
          </>
        )}
      </span>
    </div>

    <div className={styles.options}>
      <OptionRow
        type="default"
        label={`${appConfig.displayName} default`}
        description={`Use ${appConfig.displayName}'s default domain at no cost. No setup required. All shareable links open on ${appConfig.defaultPublicDomain}.`}
        isSelected={selectedOption === "default"}
        onSelect={onSelectOption("default")}
      >
        <DefaultDomainOption {...defaultOptionProps} />
      </OptionRow>

      <OptionRow
        type="subdomain"
        label="Custom subdomain"
        description={
          <>
            Use a memorable name in all your widget links, e.g.{" "}
            <strong>yourname.{appConfig.publicDomains[0]}</strong>.
          </>
        }
        isSelected={selectedOption === "subdomain"}
        onSelect={onSelectOption("subdomain")}
        allowedPlans={[PLANS.starter, PLANS.medium, PLANS.highest]}
        currentPlan={currentPlan}
      >
        <SubdomainOption {...subdomainOptionProps} />
      </OptionRow>

      <OptionRow
        type="custom"
        label="Your own domain"
        description="Connect a domain you already own for a fully branded address, e.g. links.yourschool.com."
        isSelected={selectedOption === "custom"}
        onSelect={onSelectOption("custom")}
        allowedPlans={[PLANS.medium, PLANS.highest]}
        currentPlan={currentPlan}
      >
        <CustomDomainOption {...customOptionProps} />
      </OptionRow>
    </div>

    <Card.Footer className={styles.footer}>
      <span className={styles.footerNote}>
        Old links are not redirected. Update any printed QR codes after a switch.
      </span>
      <a
        href={HELP_ARTICLE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.footerLink}
      >
        How domain setup works
        <Icon name="Share04" size={14} />
      </a>
    </Card.Footer>
  </Card>
);

export default DomainCard;
